import type { Locale } from "@/lib/i18n";

/**
 * Single source of truth for strings used by the ownership UI on the public
 * course page (pricing block, plan cards, mobile CTA bar, enroll CTA).
 *
 * Lives here — not inline in components — so the legacy-full-access UX
 * (banner + pill + "Continue Learning") can be extended/localized in one
 * place, and so that the `resolvePlanCardOwnedDisplay` unit tests don't have
 * to mirror hardcoded strings from JSX.
 *
 * Keep this module small: add keys here ONLY when a component directly
 * refactored for the B2 legacy-learner work needs them. Unrelated copy stays
 * in the component where it already lives.
 */
export type OwnershipCopy = {
  legacyBannerTitle: string;
  legacyBannerBody: string;
  /** Neutral pill shown on every plan card in the owned-legacy-full state. */
  legacyPillLabel: string;
  /** Existing "You own this plan" badge — extracted so CoursePricingBlock pulls it from here too. */
  ownedPillLabel: string;
  /** Emerald "Continue Learning" CTA label in `CourseEnrollCta`. */
  continueLearning: string;
  /** Prefix used by the upgrade CTA when joined with a localized plan name. */
  upgradePrefix: string;
};

const en: OwnershipCopy = {
  legacyBannerTitle: "You have full course access",
  // Neutral framing — the banner fires for three distinct shapes (pure legacy,
  // B1c-backfilled, pre-narrow full-scope purchase). The older "enrolled
  // before tiered plans" wording was only accurate for shape 1 and read as a
  // bug to owners of shapes 2 + 3.
  legacyBannerBody:
    "Your access covers every lesson in this course, regardless of the plans listed below.",
  legacyPillLabel: "Included in your access",
  ownedPillLabel: "Your plan",
  continueLearning: "Continue Learning",
  upgradePrefix: "Upgrade to",
};

const bn: OwnershipCopy = {
  legacyBannerTitle: "আপনার সম্পূর্ণ কোর্স অ্যাক্সেস আছে",
  legacyBannerBody:
    "নিচের প্ল্যানগুলো যাই হোক না কেন, এই কোর্সের প্রতিটা লেসন আপনার অ্যাক্সেসে আছে।",
  legacyPillLabel: "আপনার অ্যাক্সেসের অন্তর্ভুক্ত",
  ownedPillLabel: "আপনার প্ল্যান",
  continueLearning: "শিক্ষা চালিয়ে যান",
  upgradePrefix: "আপগ্রেড করুন",
};

const BY_LOCALE: Record<Locale, OwnershipCopy> = { en, bn };

export function getOwnershipCopy(locale: Locale): OwnershipCopy {
  return BY_LOCALE[locale] ?? en;
}
